// @ts-nocheck
import _ from 'lodash';

/**
 * Converts a canvas y coordinate back into a millivolt amplitude on one ECG channel. Subtracts the coordinate from that channel's baseline placement and divides by the config millivoltToPix factor.
 *
 * @param verticalChannelPlacement - The baseline y coordinate of each channel.
 * @param config - The ECG render config supplying millivoltToPix.
 * @param px - The canvas y coordinate.
 * @param chIdx - The channel index.
 * @returns The amplitude in millivolts.
 */
export const pxToMv = function (verticalChannelPlacement: any, config: any, px: any, chIdx: any) {
  const { millivoltToPix } = config;
  return (verticalChannelPlacement[chIdx] - px) / millivoltToPix;
};

/**
 * Converts a millivolt amplitude into a canvas y coordinate on one ECG channel. Subtracts the amplitude scaled by the config millivoltToPix factor from that channel's baseline placement.
 *
 * @param verticalChannelPlacement - The baseline y coordinate of each channel.
 * @param config - The ECG render config supplying millivoltToPix.
 * @param mV - The amplitude in millivolts.
 * @param chIdx - The channel index.
 * @returns The canvas y coordinate.
 */
export const mVToPx = function (verticalChannelPlacement: any, config: any, mV: any, chIdx: any) {
  const { millivoltToPix } = config;
  return verticalChannelPlacement[chIdx] - mV * millivoltToPix;
};

/**
 * Converts a sample span on the ECG strip into a duration in milliseconds. Divides the absolute sample difference by the sampling rate and scales the result by 1000.
 *
 * @param startSample - The first sample index.
 * @param endSample - The last sample index.
 * @param samplingRate - Samples per second of the recording.
 * @returns The span duration in milliseconds.
 */
export const getMeasurementFromSample = (startSample: any, endSample: any, samplingRate: any) =>
  (Math.abs(endSample - startSample) / samplingRate) * 1000;

/**
 * Converts a sample span on the ECG strip into a duration in seconds. Divides the absolute sample difference by the sampling rate.
 *
 * @param startSample - The first sample index.
 * @param endSample - The last sample index.
 * @param samplingRate - Samples per second of the recording.
 * @returns The span duration in seconds.
 */
export const getSeconds = function (startSample: any, endSample: any, samplingRate: any) {
  return Math.abs(endSample - startSample) / samplingRate;
};

/**
 * Derives the instantaneous heart rate between two R peaks. Converts the sample gap to seconds through getSeconds and divides 60 by that value.
 *
 * @param rSample - The first R peak sample index.
 * @param r2Sample - The following R peak sample index.
 * @param samplingRate - Samples per second of the recording.
 * @returns The heart rate in beats per minute.
 */
export const getBpm = function (rSample: any, r2Sample: any, samplingRate: any) {
  return 60 / getSeconds(rSample, r2Sample, samplingRate);
};

/**
 * Converts a heart rate in beats per minute into the interval between beats. Divides 60 by the given rate.
 *
 * @param rateBpm - The heart rate in beats per minute.
 * @returns The beat interval in seconds.
 */
export const bpmToSec = function (rateBpm: any) {
  return 60 / rateBpm;
};

/**
 * Reports which values occur more than once in an array. Reduces the array into a value-to-count map with a counter callback, then returns the keys whose bound count exceeds one.
 *
 * @param sourceArr - The array whose repeated values are wanted.
 * @returns The values appearing more than once, as keys.
 */
export const findDuplicateItems = function (sourceArr: any) {
  /**
   * Accumulates occurrence counts while reducing an array. Assigns onto the accumulator an incremented count keyed by the current value.
   *
   * @param prev - The accumulator mapping each value to its running count.
   * @param next - The value currently being counted.
   * @returns The updated accumulator object.
   */
  const counter = (prev: any, next: any) =>
    Object.assign(prev, {
      [next]: (prev[next] || 0) + 1,
    });
  // const singles = function(key){ return this[key] === 1 };
  /**
   * Tests whether a key occurred more than once in the bound count map. Reads the count at the key from the bound receiver and compares it against one.
   *
   * @param key - The key whose bound count is checked.
   * @returns Whether the key's count exceeds one.
   */
  const multiples = function (key: any) {
    return this[key] > 1;
  };

  const counted = sourceArr.reduce(counter, {});
  return Object.keys(counted).filter(multiples.bind(counted));
};

// Using to remove overlaps line of the grid
/**
 * Strips repeated SVG transform groups out of a rendered ECG export string. Splits the source into lines, collects the lines opening a transform group, resolves the duplicated ones through findDuplicateItems, rebuilds each three-line block from its first occurrence and removes those blocks from the source.
 *
 * @param source - The rendered SVG source string.
 * @returns The SVG source with duplicated transform groups removed.
 */
export const removeDuplicateElement = function (source: any): any {
  let result = source;
  // Pre
  const raws = _.split(source, '\n');
  /**
   * Extracts the SVG lines that open a transform group from the split source. Filters the raw lines down to those containing the `<g transform="` marker.
   *
   * @returns Array of lines that begin a transform group.
   */
  const elements = raws.filter((e) => e.includes('<g transform="'));
  // Find duplicate items
  const tmp = findDuplicateItems(elements);
  const duplicateItems: any = [];
  _.forEach(tmp, (e) => {
    /**
     * Locates the first line in the split SVG source that exactly matches a given duplicated group. Finds the index of the first raw line strictly equal to the target string.
     *
     * @returns Index of the first matching line, or -1 if none.
     */
    const fistIdx = raws.findIndex((str) => str === e);
    duplicateItems.push(`${raws[fistIdx]}\n${raws[fistIdx + 1]}\n${raws[fistIdx + 2]}`);
  });
  // Remove them
  _.forEach(duplicateItems, (str) => {
    result = result.replace(str, '');
  });
  return result;
};

/**
 * Recomputes the derived ECG rendering scale factors after a speed, gain or window change. Derives the window sample range and maximum scroll index from the sampling rate and duration, an aspect ratio from width, duration and speed, then the millivolt, seconds and sample to pixel factors plus the on-screen and export major division sizes.
 *
 * @param config - The ECG render config carrying sampling rate, window duration, width, speed, gain, export window duration and sample length.
 * @returns The recomputed scale factors and division sizes.
 */
export const reCalculateConfig = function (config: any) {
  const { samplingRate, wndDuration, width, speed, gain, exportWndDuration, sampleLength } = config;

  const sampleRange = Math.round(samplingRate * wndDuration);
  const maxIndex = sampleLength - sampleRange;
  const aspectRatioX = width / wndDuration / speed;
  const aspectRatioY = aspectRatioX;
  const millivoltToPix = gain * aspectRatioY;
  const secondsToPix = speed * aspectRatioX;
  const indexToPix = (speed * aspectRatioX) / samplingRate;
  const majorDivSize = width / wndDuration;
  const exportMajorDivSize = width / exportWndDuration;

  return {
    sampleRange,
    maxIndex,
    aspectRatioX,
    aspectRatioY,
    millivoltToPix,
    secondsToPix,
    indexToPix,
    majorDivSize,
    exportMajorDivSize,
  };
};

/**
 * Determines which ECG channel a vertical position on the strip belongs to. Computes the absolute distance from the y-point to every channel baseline and returns the index of the smallest distance.
 *
 * @param yPoint - The vertical canvas coordinate.
 * @param verticalChannelPlacement - The baseline y coordinate of each channel.
 * @returns The index of the nearest channel.
 */
export const getClosestChannelIndex = function (yPoint: any, verticalChannelPlacement: any) {
  // Math.abs(startY + endY)
  /**
   * Computes the per-channel absolute vertical distances between the given y-point and each channel baseline. Maps the channel placement array to the absolute difference of the y-point from each baseline value.
   *
   * @returns Array of absolute distances, one per channel.
   */
  const baselineDiffs = verticalChannelPlacement.map((value: any) =>
    Math.abs((2 * yPoint) / 2 - value),
  );
  return baselineDiffs.indexOf(Math.min(...baselineDiffs));
};

/**
 * Maps a numeric HES beat status onto its single-letter beat type label. Looks the number up in a table yielding V, S, Q, N and the DEL delete-beat marker, falling back to N for anything unknown.
 *
 * @param beatTypeNumber - The numeric HES beat status.
 * @returns The beat type letter, or N when unmapped.
 */
export const convertBeatTypeNumberToText = (beatTypeNumber: any) => {
  const mapObj = {
    60: 'V',
    70: 'S',
    80: 'Q',
    90: 'N',
    100: 'Q',
    1000: 'DEL', // Special case for delete beat in ecg viewer
  };
  return mapObj[beatTypeNumber] || 'N';
};

/**
 * Normalizes the normal-beat HES status to the value the ECG viewer expects. Returns 1 for status 90 and passes every other status through unchanged.
 *
 * @param hesBeatStatus - The raw HES beat status.
 * @returns The normalized beat status.
 */
export const formatHesBeatStatus = (hesBeatStatus: any) => {
  if (hesBeatStatus === 90) return 1;
  return hesBeatStatus;
};

/**
 * Reports whether the selected beat type is the delete-beat marker. Compares the beat type number against the 1000 sentinel.
 *
 * @param beatTypeNumber - The numeric beat type currently selected.
 * @returns Whether delete-beat mode is selected.
 */
export const isDeleteBeatMode = (beatTypeNumber: any) => beatTypeNumber === 1000;

/**
 * Maps a numeric HES beat status onto its beat type letter. Looks the status up in a table of V, S, Q, N and the DEL delete marker, defaulting to N.
 *
 * @param hesBeatStatus - The numeric HES beat status.
 * @returns The beat type letter, or N when unmapped.
 */
export const generateBeatTypeString = (hesBeatStatus: any) => {
  const mapObj = {
    60: 'V',
    70: 'S',
    80: 'Q',
    90: 'N',
    100: 'Q',
    1000: 'DEL', // Special case for delete beat in ecg viewer
  };
  return mapObj[hesBeatStatus] || 'N';
};

/**
 * Maps a beat type letter back onto its numeric HES beat status. Looks V, S, Q, N and DEL up in a table, defaulting to 1 for unrecognized letters.
 *
 * @param beatTypeString - The beat type letter.
 * @returns The numeric HES beat status, or 1 when unmapped.
 */
export const generateHesBeatStatus = (beatTypeString: any) => {
  const mapObj = {
    V: 60,
    S: 70,
    Q: 80,
    N: 90,
    DEL: 1000, // Special case for delete beat in ecg viewer
  };
  return mapObj[beatTypeString] || 1;
};

/**
 * Produces the CSS cursor used while the ECG viewer is in delete-beat mode. Base64-encodes an inline arrow-with-red-cross SVG into a data URL and returns it as a url cursor with a 5,5 hotspot and a crosshair fallback.
 *
 * @returns The CSS cursor value for delete-beat mode.
 */
export const getCustomCursor = () => {
  const deleteBeatCursorSvg = `<svg width="28" height="31" viewBox="0 0 28 31" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g filter="url(#filter0_d_5700_158260)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M15 8.23597L5 4L7.36515 14L10.2552 10.0503L15 8.23597Z" fill="#363B3E"/>
  <path d="M15.1786 8.70299L16.3379 8.25969L15.195 7.77557L5.19502 3.5396L4.28627 3.15466L4.51342 4.11508L6.87858 14.1151L7.12821 15.1705L7.76867 14.2953L10.5716 10.4646L15.1786 8.70299Z" stroke="white" stroke-opacity="0.8"/>
  </g>
  <g filter="url(#filter1_d_5700_158260)">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M17 26C20.866 26 24 22.866 24 19C24 15.134 20.866 12 17 12C13.134 12 10 15.134 10 19C10 22.866 13.134 26 17 26Z" fill="#F23D42"/>
  <path d="M17 26.5C21.1421 26.5 24.5 23.1421 24.5 19C24.5 14.8579 21.1421 11.5 17 11.5C12.8579 11.5 9.5 14.8579 9.5 19C9.5 23.1421 12.8579 26.5 17 26.5Z" stroke="white" stroke-opacity="0.8"/>
  </g>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M18.3333 19L21 16.3333L19.6667 15L17 17.6667L14.3333 15L13 16.3333L15.6667 19L13 21.6667L14.3333 23L17 20.3333L19.6667 23L21 21.6667L18.3333 19Z" fill="white"/>
  <defs>
  <filter id="filter0_d_5700_158260" x="0.82761" y="0.478986" width="19.5933" height="19.5225" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dy="0.915048"/>
  <feGaussianBlur stdDeviation="1.37257"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5700_158260"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5700_158260" result="shape"/>
  </filter>
  <filter id="filter1_d_5700_158260" x="6.25486" y="9.1699" width="21.4903" height="21.4903" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
  <feFlood flood-opacity="0" result="BackgroundImageFix"/>
  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
  <feOffset dy="0.915048"/>
  <feGaussianBlur stdDeviation="1.37257"/>
  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_5700_158260"/>
  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_5700_158260" result="shape"/>
  </filter>
  </defs>
  </svg>
  `;
  const parseCursor = `data:image/svg+xml;base64,${window.btoa(deleteBeatCursorSvg)}`;
  return `url(${parseCursor}) 5 5, crosshair`;
};
